import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "../../App";
import ScoreGauge from "../../components/ScoreGauge";
import SubScoreBar from "../../components/SubScoreBar";
import KTBanner from "../../components/KTBanner";
import PartnerAd from "../../components/PartnerAd";
import { Button } from "../../components/ui/button";
import { Printer, ShoppingBag, ArrowLeft, QrCode } from "lucide-react";

const DIMS = [
  "scalp_balance", "hydration_need", "structural_damage", "reconstruction_need",
  "heat_exposure", "protection_need", "buildup_tendency", "shedding_concern",
];

const DIM_LABEL = {
  scalp_balance: { en: "Scalp balance", it: "Equilibrio cuoio" },
  hydration_need: { en: "Hydration need", it: "Bisogno idratazione" },
  structural_damage: { en: "Structural damage", it: "Danno strutturale" },
  reconstruction_need: { en: "Reconstruction need", it: "Bisogno ricostruzione" },
  heat_exposure: { en: "Heat exposure", it: "Esposizione al calore" },
  protection_need: { en: "Protection need", it: "Bisogno protezione" },
  buildup_tendency: { en: "Buildup tendency", it: "Tendenza al residuo" },
  shedding_concern: { en: "Density concern", it: "Percezione densità" },
};

const qrUrl = (link) => `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(link)}`;

export default function SalonResult() {
  const nav = useNavigate();
  const [payload, setPayload] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("salon_result");
    if (!raw) { nav("/salon/dashboard"); return; }
    try {
      setPayload(JSON.parse(raw));
    } catch { nav("/salon/dashboard"); return; }
    axios.get(`${API}/salons/dashboard`).then((r) => setDashboard(r.data)).catch(() => {});
  }, [nav]);

  const lang = payload?.language || "en";
  const full = payload?.full_analysis || {};
  const flow = payload?.flow || "female";

  const shopLink = useMemo(() => {
    if (!dashboard) return "";
    return dashboard.salon_link;
  }, [dashboard]);

  const trackClick = async (productLine) => {
    try {
      await axios.post(`${API}/salons/track/click`, {
        product_id: "", product_category: productLine || "",
      });
    } catch {}
  };

  if (!payload) return null;
  const isMale = flow === "male";

  return (
    <div className="min-h-screen bg-white" data-testid="salon-result">
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .kt-shadow, .kt-shadow-md { box-shadow: none !important; }
          body { background: white !important; }
        }
      `}</style>

      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b kt-line no-print">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/salon/dashboard" data-testid="salon-result-back"
            className="inline-flex items-center gap-2 text-[11px] kt-tracking-wider uppercase text-[#383633]">
            <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => window.print()} data-testid="print-result"
              className="rounded-full border-[#383633] text-[#383633] hover:bg-[#F8F6F2] text-xs kt-tracking uppercase h-9">
              <Printer className="h-3.5 w-3.5" /><span className="ml-2">Print / PDF</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Branded header */}
        <div className="text-center mb-10 pb-8 border-b kt-line">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">HAIRTU · Powered by KT MILANO</div>
          <div className="font-serif text-3xl text-[#383633] tracking-tight mt-2">Professional Italian Haircare</div>
          <div className="text-[11px] text-[#6F6B66] mt-3">
            Anonymous cosmetic result · {isMale ? "Man path" : "Woman path"} ·{" "}
            {new Date().toLocaleDateString(lang)}
          </div>
        </div>

        {/* Small KT Milano platform banner */}
        <div className="mb-8">
          <KTBanner size="small" lang={lang}
            salonSlug={dashboard?.salon_slug || ""}
            refParam={dashboard?.referral_param || "hairtu_salon"} />
        </div>

        {/* Banner (admin controlled) */}
        {dashboard?.banner?.active && (dashboard.banner.en || dashboard.banner.it) && (
          <div className="mb-8 rounded-2xl bg-[#F8F6F2] border kt-line p-5 text-sm text-[#383633]" data-testid="result-banner">
            {(lang === "it" ? dashboard.banner.it : dashboard.banner.en) || dashboard.banner.en}
          </div>
        )}

        {/* Score + profile */}
        <div className="grid sm:grid-cols-[auto,1fr] gap-8 items-center">
          <ScoreGauge value={full.overall_score || 0}
            label={lang === "it" ? "Punteggio cosmetico" : "Cosmetic Score"} />
          <div>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
              {isMale
                ? (lang === "it" ? "Profilo cosmetico" : "Cosmetic profile")
                : (lang === "it" ? "Il profilo" : "The profile")}
            </div>
            {isMale ? (
              <>
                <div className="font-serif text-2xl text-[#383633] mb-2" data-testid="result-profile-name">
                  {full.profile_name}
                </div>
                <p className="text-sm text-[#6F6B66] leading-relaxed">{full.main_concern}</p>
              </>
            ) : (
              <p className="text-[#383633] text-lg font-serif leading-relaxed" data-testid="result-profile-summary">
                {full.profile_summary}
              </p>
            )}
          </div>
        </div>

        {/* Subscores — female only */}
        {!isMale && full.scores && (
          <div className="mt-14">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6">
              {lang === "it" ? "Sotto-punteggi" : "Sub-scores"}
            </div>
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5" data-testid="result-subscores">
              {DIMS.map((d, i) => (
                <SubScoreBar
                  key={d}
                  label={DIM_LABEL[d]?.[lang] || DIM_LABEL[d]?.en || d}
                  value={full.scores[d] || 0}
                  delay={i * 0.03}
                />
              ))}
            </div>
          </div>
        )}

        {/* Priorities — female */}
        {!isMale && (full.top_priorities || []).length > 0 && (
          <div className="mt-14">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6">
              {lang === "it" ? "Priorità cosmetiche" : "Cosmetic priorities"}
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {full.top_priorities.map((p, i) => (
                <div key={i} className="bg-white border kt-line rounded-2xl p-5 kt-shadow">
                  <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">0{i + 1}</div>
                  <div className="font-serif text-lg text-[#383633] tracking-tight mb-2">{p.title}</div>
                  <div className="text-sm text-[#6F6B66] leading-relaxed">{p.reason}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Male paid guidance */}
        {isMale && (
          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            {full.morning_guidance && (
              <ResultCard label={lang === "it" ? "Ogni mattina" : "Morning / daily"} text={full.morning_guidance} />
            )}
            {full.wash_day_guidance && (
              <ResultCard label={lang === "it" ? "Giorno di lavaggio" : "Wash day"} text={full.wash_day_guidance} />
            )}
            {full.styling_buildup_guidance && (
              <ResultCard label={lang === "it" ? "Styling & residui" : "Styling & buildup"} text={full.styling_buildup_guidance} />
            )}
            {full.progress_checkpoints && (
              <ResultCard label={lang === "it" ? "Progresso" : "Progress"} text={full.progress_checkpoints} />
            )}
          </div>
        )}

        {/* Recommended KT Milano routine */}
        {(full.recommendations || []).length > 0 && (
          <div className="mt-16">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">
              {lang === "it" ? "Routine KT Milano personalizzata" : "Personalized KT Milano routine"}
            </div>
            <p className="text-[#6F6B66] max-w-lg mb-8 text-sm">
              {lang === "it"
                ? "Fotografie ufficiali dei prodotti KT Milano. Guida d'uso e sequenza."
                : "Official KT Milano product photographs. Usage guidance and sequence."}
            </p>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="result-recommendations">
              {full.recommendations.map((p) => (
                <div key={p.id} className="bg-white border kt-line rounded-2xl overflow-hidden kt-shadow" data-testid={`salon-product-${p.id}`}>
                  {p.image && (
                    <div className="h-40 bg-[#F3F3F3] overflow-hidden">
                      <img src={p.image} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-1">{p.line}</div>
                    <div className="font-serif text-lg text-[#383633] tracking-tight mb-2">{p.name}</div>
                    <div className="text-sm text-[#6F6B66] leading-relaxed mb-2">{p.usage}</div>
                    <div className="text-[11px] text-[#383633]">
                      <span className="text-[#6F6B66]">
                        {lang === "it" ? "Frequenza" : "Frequency"}:
                      </span> {p.frequency}
                    </div>
                    {shopLink && (
                      <a
                        href={shopLink}
                        target="_blank" rel="noreferrer"
                        onClick={() => trackClick(p.line)}
                        data-testid={`shop-product-${p.id}`}
                        className="no-print mt-3 inline-flex items-center gap-2 text-xs kt-tracking uppercase text-[#383633] hover:underline"
                      >
                        <ShoppingBag className="h-3.5 w-3.5" /> Shop KT Milano
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shop CTA + QR */}
        {shopLink && (
          <div className="mt-16 rounded-3xl bg-[#F8F6F2] border kt-line p-8 kt-shadow grid sm:grid-cols-[1fr,auto] gap-6 items-center" data-testid="shop-routine-card">
            <div>
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
                {lang === "it" ? "Acquista la routine" : "Shop the recommended routine"}
              </div>
              <h2 className="font-serif text-2xl text-[#383633] tracking-tight mb-3">
                {lang === "it"
                  ? "Prendi la tua routine KT Milano — direttamente dal salone."
                  : "Take your KT Milano routine home — straight from the salon."}
              </h2>
              <a
                href={shopLink}
                target="_blank" rel="noreferrer"
                onClick={() => trackClick("shop_cta")}
                data-testid="shop-routine-cta"
                className="no-print inline-flex items-center gap-2 rounded-full bg-[#383633] hover:bg-[#4A4238] text-white px-6 py-3 text-xs kt-tracking uppercase"
              >
                <ShoppingBag className="h-3.5 w-3.5" /> Shop the Recommended Routine →
              </a>
            </div>
            <div className="text-center">
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2 flex items-center justify-center gap-1.5">
                <QrCode className="h-3.5 w-3.5" /> Shopify
              </div>
              <img alt="Shop QR" src={qrUrl(shopLink)} loading="lazy" decoding="async" className="mx-auto rounded-xl border kt-line" data-testid="shop-qr" />
            </div>
          </div>
        )}

        {/* 30-day plan — female only */}
        {!isMale && (full.plan_30_days || []).length > 0 && (
          <div className="mt-16">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6">
              {lang === "it" ? "Piano 30 giorni" : "30-Day Plan"}
            </div>
            <div className="grid gap-3" data-testid="result-plan">
              {full.plan_30_days.map((d, i) => (
                <div key={i} className="bg-white border kt-line rounded-2xl p-4 flex gap-4">
                  <div className="shrink-0 w-14">
                    <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Day</div>
                    <div className="font-serif text-2xl text-[#383633]">{String(d.day).padStart(2, "0")}</div>
                  </div>
                  <div>
                    <div className="font-serif text-base text-[#383633]">{d.title}</div>
                    <div className="text-xs text-[#6F6B66] leading-relaxed">{d.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 pt-6 border-t kt-line text-center">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">HAIRTU — Powered by KT MILANO</div>
          <div className="text-[10px] text-[#6F6B66] mt-1">
            Professional Italian Haircare · Anonymous cosmetic result
          </div>
        </div>

        {/* Partner ad — anonymous salon result, below KT routine */}
        <PartnerAd
          placement="salon_result"
          language={lang}
          audience="salon"
          profile={isMale ? (full.profile_name || "") : ((full.top_priorities && full.top_priorities[0]?.title) || "")}
          salonSlug={dashboard?.salon_slug || ""}
        />

        {/* PDF footer strip — visible in print too */}
        <div className="mt-6 text-center text-[10px] text-[#6F6B66] leading-relaxed" data-testid="pdf-footer">
          HAIRTU — Powered by KT MILANO · Professional Italian Haircare · www.ktmilano.com
        </div>
      </div>
    </div>
  );
}

function ResultCard({ label, text }) {
  return (
    <div className="bg-white border kt-line rounded-2xl p-5">
      <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">{label}</div>
      <p className="text-sm text-[#383633] leading-relaxed">{text}</p>
    </div>
  );
}
